import { Component, HostListener, ViewChild, ElementRef, Output, EventEmitter, AfterContentChecked, Input } from '@angular/core';
import html2canvas from 'html2canvas';

@Component({
  selector: 'meta-liquid-cut',
  templateUrl: './liquid-cut.component.html',
  styleUrls: ['./liquid-cut.component.scss']
})
export class LiquidCutComponent implements AfterContentChecked {
  @ViewChild('cutContainer', { static: true }) cutContainer!: ElementRef;
  
  @Input() liquidPage: ElementRef | undefined;

  @Output() createdCanvasDataUrl: EventEmitter<string> = new EventEmitter();

  showLoader: boolean = false;
  showCutContainer: boolean = true;

  marker!: HTMLElement;

  initialited: boolean = false;
  cutContainerHeight: number = 0;
  cutContainerWidth: number = 0;
  cutContainerLeft: string | number = 0;

  selection = {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  };

  private isSelecting = false;
  private startX = 0;
  private startY = 0;

  ngAfterContentChecked(): void {
    if (!this.liquidPage || !this.liquidPage.nativeElement || this.initialited) {
      return;
    }

    const pageStyle = getComputedStyle(this.liquidPage.nativeElement);
    this.cutContainerLeft = pageStyle.marginLeft ?? 0;
    this.cutContainerHeight = this.liquidPage.nativeElement.clientHeight ?? 0;
    this.cutContainerWidth = this.liquidPage.nativeElement.clientWidth ?? 0;
    this.marker = this.cutContainer.nativeElement;
    this.initialited = true;
  }

  createCanvas() {
    return new Promise<HTMLCanvasElement | undefined>((resolve) => {
      if (this.selection.width <= 0 && this.selection.height <= 0) {
        resolve(undefined);
        return;
      }
      if (this.liquidPage?.nativeElement) {
        this.showCutContainer = false;
        this.showLoader = true;
        html2canvas(this.liquidPage?.nativeElement, {
          scale: 1,
          useCORS: true,
        }).then((html2CanvasResult) => {
          const html2CanvasCtx = html2CanvasResult.getContext('2d')!;
          const data = html2CanvasCtx.getImageData(
            this.selection.left,
            this.selection.top,
            this.selection.width,
            this.selection.height,
          );

          const canvasElement = document.createElement('canvas');
          const canvasElementCtx = canvasElement.getContext('2d');
          canvasElement.width = this.selection.width;
          canvasElement.height = this.selection.height;
          canvasElementCtx?.putImageData(data!, 0, 0);

          this.showLoader = false;
          resolve(canvasElement);
        });
      }
    });
  }

  private manageEvent(e: MouseEvent | TouchEvent) {
    const liquidPageTag = document.getElementsByTagName('meta-liquid-page')[0] as HTMLElement;
    const liquidPageTagOffsetTop = liquidPageTag.offsetTop ?? 0;
    const liquidPageTagOffsetLeft = liquidPageTag.offsetLeft ?? 0;
    const liquidPageIdStyle = getComputedStyle(this.liquidPage?.nativeElement);
    const liquidPageIdMarginLeft = liquidPageIdStyle.marginLeft ?? '0';
    const y = (e as MouseEvent).y;
    const x = (e as MouseEvent).x;
    const top = y - liquidPageTagOffsetTop;
    const left = x - liquidPageTagOffsetLeft - parseFloat(liquidPageIdMarginLeft);
    return {
      top,
      left,
      width: this.selection.width,
      height: this.selection.height,
    }
  }

  private _handleCutStart(e: MouseEvent | TouchEvent) {
    const coordinates = this.manageEvent(e);
    if (
      coordinates.top >= 0 && coordinates.left >= 0 &&
      coordinates.top < this.marker.clientHeight &&
      coordinates.left < this.marker.clientWidth
    ) {
      e.preventDefault();
      e.stopPropagation();
      this.selection = coordinates;
      this.startX = coordinates.left;
      this.startY = coordinates.top;
      this.isSelecting = true;
    }
  }

  private _handleCutMove(e: MouseEvent | TouchEvent) {
    if (this.isSelecting) {
      e.preventDefault();
      e.stopPropagation();

      const coordinates = this.manageEvent(e);
      let height = Math.abs(coordinates.top - this.startY);
      let width = Math.abs(coordinates.left - this.startX);
      coordinates.height = height > this.marker.clientHeight - 2 ? this.marker.clientHeight - 2 : height;
      coordinates.width = width > this.marker.clientWidth - 2 ? this.marker.clientWidth - 2 : width;

      if (coordinates.height > 1 && coordinates.width > 1) {
        this.selection = {
          top: coordinates.top - this.startY < 0 ? coordinates.top : this.startY,
          left: coordinates.left - this.startX < 0 ? coordinates.left : this.startX,
          width: coordinates.width,
          height: coordinates.height,
        }
      }
    }
  }

  private _handleCutEnd() {
    const marker: HTMLElement = document.getElementsByClassName("selection-marquee")[0] as HTMLElement;
    if (marker) {
      marker.style.display = "none";
    }
    this.isSelecting = false;
    if (this.selection.height > 0 && this.selection.width > 0) {
      this.createCanvas().then((canvas) => {
        this.isSelecting = false;
        this.selection = {
          top: 0,
          left: 0,
          width: 0,
          height: 0,
        };
        if (canvas) {
          this.createdCanvasDataUrl.emit(canvas.toDataURL());
        }
      });
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    this._handleCutStart(event);
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    this._handleCutEnd();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    this._handleCutMove(event);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (!this.liquidPage || !this.liquidPage.nativeElement) {
      return;
    }
    this.cutContainerHeight = this.liquidPage.nativeElement.clientHeight ?? 0;
    this.cutContainerWidth = this.liquidPage.nativeElement.clientWidth ?? 0;
  }
}
